import { memo, useCallback, useMemo, useRef, useState } from "react";
import { View, Text, Button, Card } from "@src/components";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { Stack, useLocalSearchParams, useRouter } from "expo-router";
import type { CameraUser, SiteUser } from "@src/features/settings/data/mock-sites";
import { PressableScale } from "pressto";
import { Icon } from "@src/components/Icon";
import { ActivityIndicator, Pressable, ScrollView } from "react-native";
import { useActionSheet } from "@expo/react-native-action-sheet";
import { Section } from "@src/features/settings/components/Section";
import { UserSitesList } from "@src/features/settings/components/UserSitesList";
import { showSuccessToast, showErrorToast, showInfoToast } from "@src/utils/toast";
import { UserCamerasList } from "@src/features/settings/components/UserCamerasList";
import { EditUserBottomSheet } from "@src/features/settings/components/EditUserBottomSheet";
import { TenantAdminBottomSheet } from "@src/features/settings/components/TenantAdminBottomSheet";
import { TenantAdminConfirmContent } from "@src/features/settings/components/TenantAdminConfirmContent";
import type { BottomSheetModal } from "@gorhom/bottom-sheet";
import { RemoveAccessBottomSheet } from "@src/features/settings/components/RemoveAccessBottomSheet";
import { RemoveAccessContent } from "@src/features/settings/components/RemoveAccessContent";
import Animated, {
  FadeIn,
  FadeOut,
  LinearTransition,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import {
  useAddCameraPermission,
  useCreateRoleAssignment,
  useDeleteCameraPermission,
  useDeleteRoleAssignment,
  useRevokeUserAccess,
  type AccessManagementRoleAssignment,
  type RevokeUserAccessQuery,
  type DeleteCameraPermissionRequest,
} from "@visioni/a3s-access/app";
import type { CameraRole } from "@visioni/a3s-access/schemas";
import { useCameras } from "@src/features/cameras";
import { buildSiteAccessList } from "@src/features/settings/utils";
import { useSelfActionGuard, useUserRoles, useUsersAndPermissionsData } from "@src/features/settings/hooks";
import { getCameraKeyString } from "@src/utils/key-parser";
import type { Camera } from "@src/features/settings/data/mock-sites";
import { WebLayout } from "@src/components/web/WebLayout.web";
import { isWeb } from "@src/utils/web-helpers";
// @ts-expect-error baseline 2026-02-16
import { RightDrawer } from "@src/components/web";

const CameraAccessScreen = memo(() => {
  const { theme } = useUnistyles();
  const { userId } = useLocalSearchParams<{ userId: string }>();

  const router = useRouter();
  const { showActionSheetWithOptions } = useActionSheet();
  const editUserBottomSheetRef = useRef<BottomSheetModal>(null);
  const tenantAdminBottomSheetRef = useRef<BottomSheetModal>(null);
  const removeAccessBottomSheetRef = useRef<BottomSheetModal>(null);
  const [removeAccessConfig, setRemoveAccessConfig] = useState<{
    title: string;
    message: string;
    onConfirm: () => void;
  } | null>(null);

  const {
    isReady: isUsersAndPermsReady,
    isError: isUsersAndPermsError,
    accessData: data,
  } = useUsersAndPermissionsData();
  const { data: cameras } = useCameras();
  const createRoleAssignment = useCreateRoleAssignment();
  const deleteRoleAssignment = useDeleteRoleAssignment();
  const revokeUserAccess = useRevokeUserAccess();
  const addCameraPermission = useAddCameraPermission();
  const deleteCameraPermission = useDeleteCameraPermission();

  const { isSelf, guard: guardSelfAction } = useSelfActionGuard(userId);

  const sites = useMemo(() => (data ? buildSiteAccessList(data, cameras) : []), [data, cameras]);

  const user = useMemo(() => {
    const fromSites = sites.flatMap((s) => s.users).find((u) => u.id === userId);
    if (fromSites) return fromSites;
    // TODO: this screen needs to be refactored to be tenant-scoped, not site-scoped, so we can remove this fallback.
    const raw = data?.users.find((u) => String(u.id) === userId);
    if (!raw) return undefined;
    return {
      id: String(raw.id),
      name: [raw.givenName, raw.familyName].filter(Boolean).join(" ") || raw.email,
      email: raw.email,
      phoneNumber: raw.phoneNumber ?? "",
      role: "viewer" as const,
    };
  }, [sites, userId, data]);

  const { isTenantAdmin } = useUserRoles(userId);

  const titleScreen = useMemo(() => {
    if (user?.name) return user.name;
    return user?.email;
  }, [user]);

  // Map full cameraId (e.g. "<siteId>/<asset>@<id>") → CameraApiResponse, for resolving names from permission assignments.
  const cameraById = useMemo(() => {
    const built = new Map<string, NonNullable<typeof cameras>[number]>();
    if (!cameras) return built;
    for (const c of cameras) {
      built.set(`${c.site.id}/${getCameraKeyString(c)}`, c);
    }
    return built;
  }, [cameras]);

  const userCameras = useMemo<Camera[]>(() => {
    if (!data || !userId) return [];
    return data.cameras
      .filter((a) => String(a.user) === userId)
      .flatMap((a) => {
        const c = cameraById.get(a.camera);
        const displayName = c?.nickname ?? c?.name ?? (c ? getCameraKeyString(c) : a.camera);
        return [
          {
            id: a.camera,
            name: displayName,
            users: [
              {
                id: userId,
                name: user?.name ?? "",
                role: a.role === "camera_manager" ? ("manager" as const) : ("viewer" as const),
              },
            ],
          },
        ];
      });
  }, [data, userId, cameraById, user?.name]);

  const [isUpdatingRole, setIsUpdatingRole] = useState(false);
  const [roleOverrides, setRoleOverrides] = useState<Map<string, SiteUser["role"]>>(new Map());

  const userSites = useMemo(
    () =>
      sites
        .filter((s) => s.users.some((u) => u.id === userId))
        .map((s) => ({
          ...s,
          users: s.users
            .filter((u) => u.id === userId)
            .map((u) => {
              const override = roleOverrides.get(s.id);
              return override ? { ...u, role: override } : u;
            }),
        })),
    [sites, userId, roleOverrides],
  );

  const [showList, setShowList] = useState(false);
  const chevronRotation = useSharedValue(0);

  const handleSiteRoleChange = useCallback(
    async (siteId: string, targetUserId: string, newRole: SiteUser["role"]) => {
      const site = userSites.find((s) => s.id === siteId);
      const siteUser = site?.users.find((u) => u.id === targetUserId);
      if (!siteUser || siteUser.role === newRole) return;

      // Optimistic update + loader
      setRoleOverrides((prev) => new Map(prev).set(siteId, newRole));
      setTimeout(() => {
        setIsUpdatingRole(true);
      }, 500);
      showSuccessToast({ message: "User role updated" });

      const oldAssignment = {
        user: targetUserId,
        role: siteUser.role === "manager" ? `site_manager:${siteId}` : `site_user:${siteId}`,
      } as AccessManagementRoleAssignment;
      const newAssignment = {
        user: targetUserId,
        role: newRole === "manager" ? `site_manager:${siteId}` : `site_user:${siteId}`,
      } as AccessManagementRoleAssignment;

      try {
        await deleteRoleAssignment.mutateAsync(oldAssignment);
        await createRoleAssignment.mutateAsync(newAssignment);
        // Wait for query refetch to settle before hiding loader
        await new Promise((resolve) => setTimeout(resolve, 1500));
      } catch {
        setRoleOverrides((prev) => {
          const next = new Map(prev);
          next.delete(siteId);
          return next;
        });
        showErrorToast({ message: "Failed to update user role" });
      } finally {
        setIsUpdatingRole(false);
      }
    },
    [userSites, deleteRoleAssignment, createRoleAssignment],
  );

  const handleRevokeUserAccess = useCallback(async () => {
    if (guardSelfAction()) return;
    if (!userId) return;

    try {
      await revokeUserAccess.mutateAsync({ user: userId } as RevokeUserAccessQuery);
      showSuccessToast({ message: "Access revoked" });
      router.back();
    } catch {
      showErrorToast({ message: "Failed to revoke access" });
    }
  }, [guardSelfAction, userId, revokeUserAccess, router]);

  const handleWebRevokeAccessPress = useCallback(() => {
    setRemoveAccessConfig({
      title: "Revoke access",
      message: "This will revoke all of this user's access to this organisation. This action cannot be undone.",
      onConfirm: () => {
        void handleRevokeUserAccess();
      },
    });
    setIsRemoveAccessDrawerVisible(true);
  }, [handleRevokeUserAccess]);

  const isEditUserEnabled = false; // TODO: enable when user profile update API is available

  const handleMenuPress = useCallback(() => {
    const baseOptions = isEditUserEnabled ? ["Edit User"] : [];
    const revokeOption = !isSelf ? ["Revoke Access"] : [];
    const options = [...baseOptions, ...revokeOption, "Cancel"];
    const cancelButtonIndex = options.length - 1;
    const destructiveButtonIndex = !isSelf ? baseOptions.length : undefined;

    const presentRevokeConfirmation = () => {
      setRemoveAccessConfig({
        title: "Revoke access",
        message: "This will revoke all of this user's access to this organisation. This action cannot be undone.",
        onConfirm: () => {
          void handleRevokeUserAccess();
        },
      });
      setTimeout(() => removeAccessBottomSheetRef.current?.present(), 100);
    };

    showActionSheetWithOptions(
      {
        options,
        cancelButtonIndex,
        destructiveButtonIndex,
      },
      (selectedIndex?: number) => {
        if (selectedIndex === undefined || selectedIndex === cancelButtonIndex) return;
        const picked = options[selectedIndex];
        if (picked === "Edit User") editUserBottomSheetRef.current?.present();
        else if (picked === "Revoke Access") presentRevokeConfirmation();
      },
    );
  }, [showActionSheetWithOptions, handleRevokeUserAccess, isEditUserEnabled, isSelf]);

  // Web-only: RightDrawer state for the "tenant admin" confirmation (on native the bottom sheet is used instead).
  const [isTenantAdminDrawerVisible, setIsTenantAdminDrawerVisible] = useState(false);

  const handleCloseTenantAdminDrawer = useCallback(() => {
    setIsTenantAdminDrawerVisible(false);
  }, []);

  // Web-only: RightDrawer state for the remove-access confirmation (on native the bottom sheet is used instead).
  const [isRemoveAccessDrawerVisible, setIsRemoveAccessDrawerVisible] = useState(false);

  const handleCloseRemoveAccessDrawer = useCallback(() => {
    setIsRemoveAccessDrawerVisible(false);
  }, []);

  const handleWebRemoveAccessConfirm = useCallback(() => {
    removeAccessConfig?.onConfirm();
    setIsRemoveAccessDrawerVisible(false);
  }, [removeAccessConfig]);

  const handleTenantAdminPress = useCallback(() => {
    if (isWeb) {
      setIsTenantAdminDrawerVisible(true);
      return;
    }
    tenantAdminBottomSheetRef.current?.present();
  }, []);

  const handleRemoveTenantAdmin = useCallback(() => {
    if (guardSelfAction()) return;

    setRemoveAccessConfig({
      title: "Remove organisation admin",
      message: "Are you sure you want to remove organisation admin access from this user?",
      onConfirm: () => {
        const assignment = {
          user: userId,
          role: "tenant_admin",
        } as AccessManagementRoleAssignment;

        deleteRoleAssignment.mutate(assignment, {
          onSuccess: () => showSuccessToast({ message: "Organisation admin access removed" }),
          onError: () => showErrorToast({ message: "Failed to remove organisation admin access" }),
        });
      },
    });
    if (isWeb) {
      setIsRemoveAccessDrawerVisible(true);
      return;
    }
    setTimeout(() => removeAccessBottomSheetRef.current?.present(), 100);
  }, [userId, deleteRoleAssignment, guardSelfAction]);

  const handleTenantAdminConfirm = useCallback(() => {
    if (isTenantAdmin) {
      showInfoToast({ message: "This user is already an organisation admin" });
      return;
    }

    const assignment = {
      user: userId,
      role: "tenant_admin",
    } as AccessManagementRoleAssignment;

    createRoleAssignment.mutate(assignment, {
      onSuccess: () => showSuccessToast({ message: "Organisation admin access granted" }),
      onError: () => showErrorToast({ message: "Failed to grant organisation admin access" }),
    });
  }, [userId, isTenantAdmin, createRoleAssignment]);

  const handleWebTenantAdminConfirm = useCallback(() => {
    handleTenantAdminConfirm();
    setIsTenantAdminDrawerVisible(false);
  }, [handleTenantAdminConfirm]);

  const handleEditUserSave = useCallback((data: { email: string }) => {
    // TODO: persist user edit
    showSuccessToast({ message: "User details updated" });
  }, []);

  const handleCameraRoleChange = useCallback(
    (cameraId: string, targetUserId: string, newRole: CameraUser["role"]) => {
      const role: CameraRole = newRole === "manager" ? "camera_manager" : "camera_viewer";
      addCameraPermission.mutate(
        { user: targetUserId, camera: cameraId, role },
        {
          onSuccess: () => showSuccessToast({ message: "Camera role updated" }),
          onError: () => showErrorToast({ message: "Failed to update camera role" }),
        },
      );
    },
    [addCameraPermission],
  );

  const handleDeleteSite = useCallback(
    (siteId: string) => {
      const site = userSites.find((s) => s.id === siteId);
      const siteUser = site?.users[0];
      if (!siteUser) return;

      setRemoveAccessConfig({
        title: "Remove user from site",
        message: `Removing this permission will completely remove the user from ${site?.name ?? "the site"}.`,
        onConfirm: () => {
          const assignment = {
            user: siteUser.id,
            role: siteUser.role === "manager" ? `site_manager:${siteId}` : `site_user:${siteId}`,
          } as AccessManagementRoleAssignment;

          deleteRoleAssignment.mutate(assignment, {
            onSuccess: () => showSuccessToast({ message: "User removed from site" }),
            onError: () => showErrorToast({ message: "Failed to remove user from site" }),
          });
        },
      });
      if (isWeb) {
        setIsRemoveAccessDrawerVisible(true);
        return;
      }
      setTimeout(() => removeAccessBottomSheetRef.current?.present(), 100);
    },
    [userSites, deleteRoleAssignment],
  );

  const handleDeleteCamera = useCallback(
    (cameraId: string) => {
      const cameraName = userCameras.find((c) => c.id === cameraId)?.name ?? "camera";
      setRemoveAccessConfig({
        title: "Remove camera access",
        message: `This user will lose access to ${cameraName}.`,
        onConfirm: () => {
          deleteCameraPermission.mutate({ user: userId, camera: cameraId } as DeleteCameraPermissionRequest, {
            onSuccess: () => showSuccessToast({ message: "Camera access removed" }),
            onError: () => showErrorToast({ message: "Failed to remove camera access" }),
          });
        },
      });
      if (isWeb) {
        setIsRemoveAccessDrawerVisible(true);
        return;
      }
      setTimeout(() => removeAccessBottomSheetRef.current?.present(), 100);
    },
    [userCameras, userId, deleteCameraPermission],
  );

  const onPressAddAccess = useCallback(() => {
    setShowList((prev) => {
      chevronRotation.value = withTiming(prev ? 0 : 1, { duration: 200 });
      return !prev;
    });
  }, [chevronRotation]);

  const animatedChevronIconStyle = useAnimatedStyle(() => {
    return {
      transform: [{ rotate: `${chevronRotation.value * 180}deg` }],
    };
  });

  if (isUsersAndPermsError) {
    if (isWeb) {
      return (
        <>
          <Stack.Screen options={{ headerShown: false }} />
          <WebLayout>
            <View style={styles.centered}>
              <Text textStyle="sm/regular" color="textMuted" textAlign="center">
                Something went wrong. Please try again.
              </Text>
            </View>
          </WebLayout>
        </>
      );
    }
    return (
      <>
        <Stack.Screen
          options={{
            title: "",
            headerBackButtonDisplayMode: "minimal",
            headerStyle: { backgroundColor: theme.colors.background },
          }}
        />
        <View style={styles.centered}>
          <Text textStyle="sm/regular" color="textMuted" textAlign="center">
            Something went wrong. Please try again.
          </Text>
        </View>
      </>
    );
  }

  if (!isUsersAndPermsReady) {
    if (isWeb) {
      return (
        <>
          <Stack.Screen options={{ headerShown: false }} />
          <WebLayout>
            <View style={styles.centered}>
              <ActivityIndicator />
            </View>
          </WebLayout>
        </>
      );
    }
    return (
      <>
        <Stack.Screen
          options={{
            title: "Loading...",
            headerBackButtonDisplayMode: "minimal",
            headerStyle: { backgroundColor: theme.colors.background },
          }}
        />
        <View style={styles.centered}>
          <ActivityIndicator />
        </View>
      </>
    );
  }

  if (isWeb) {
    return (
      <>
        <Stack.Screen options={{ headerShown: false }} />
        <WebLayout>
          <ScrollView style={styles.scrollView} contentContainerStyle={styles.contentContainerStyle}>
            <View flexDirection="row" alignItems="center" justifyContent="space-between" width="50%">
              <View flexDirection="row" alignItems="center" gap="2xl">
                <Text textStyle="lg/semibold" color="text">
                  {titleScreen}
                </Text>
                <View>
                  {user?.email && user?.name && (
                    <Text textStyle="xxs/regular" color="textMuted" textAlign="right">
                      {user?.email}
                    </Text>
                  )}
                  {user?.phoneNumber && user?.email && user?.name && (
                    <Text textStyle="xxs/regular" color="textMuted" textAlign="right">
                      {user?.phoneNumber}
                    </Text>
                  )}
                </View>
              </View>
              {!isSelf && (
                <PressableScale onPress={handleWebRevokeAccessPress}>
                  <Icon name="delete" size={24} color={theme.colors.text} />
                </PressableScale>
              )}
            </View>
            <Card style={{ width: "50%", gap: theme.spacing["3xl"] }}>
              {isTenantAdmin && (
                <View
                  bg="surfaceCard"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                  style={styles.tenantAdminCard}
                >
                  <Text textStyle="sm/regular" color="text">
                    Organisation admin
                  </Text>
                  {!isSelf && (
                    <PressableScale onPress={handleRemoveTenantAdmin}>
                      <Icon name="delete" size={24} color={theme.colors.textSecondary} />
                    </PressableScale>
                  )}
                </View>
              )}
              {!isTenantAdmin && (
                <Section title="Site access">
                  {isUpdatingRole ?
                    <Animated.View entering={FadeIn} exiting={FadeOut}>
                      <ActivityIndicator style={{ paddingVertical: theme.spacing["5xl"] }} />
                    </Animated.View>
                  : <Animated.View entering={FadeIn} exiting={FadeOut}>
                      <UserSitesList
                        sites={userSites}
                        onSiteRoleChange={handleSiteRoleChange}
                        onDeleteSite={handleDeleteSite}
                      />
                    </Animated.View>
                  }
                </Section>
              )}

              {!isTenantAdmin && (
                <Section title="Camera access">
                  <UserCamerasList
                    cameras={userCameras}
                    onCameraRoleChange={handleCameraRoleChange}
                    onDeleteCamera={handleDeleteCamera}
                  />
                </Section>
              )}
              {!isTenantAdmin && (
                <Animated.View layout={LinearTransition}>
                  <Button
                    size="md"
                    variant="primary"
                    label="Add access"
                    onPress={onPressAddAccess}
                    iconRight={
                      <Animated.View style={animatedChevronIconStyle}>
                        <Icon name="chevronDown" color={theme.colors.white.default} />
                      </Animated.View>
                    }
                  />
                  {showList && (
                    <Animated.View entering={FadeIn} exiting={FadeOut} style={styles.listContainer}>
                      <Pressable
                        onPress={handleTenantAdminPress}
                        style={({ pressed }) => [styles.listItem, pressed && styles.pressed]}
                      >
                        <Text textStyle="xs/regular" color="textMuted">
                          Organisation admin
                        </Text>
                      </Pressable>
                      <Pressable
                        onPress={() =>
                          router.push({
                            pathname: "/settings/site-access/camera-access/assign-sites",
                            params: { userId },
                          })
                        }
                        style={({ pressed }) => [styles.listItem, pressed && styles.pressed]}
                      >
                        <Text textStyle="xs/regular" color="textMuted">
                          Site access
                        </Text>
                      </Pressable>
                    </Animated.View>
                  )}
                </Animated.View>
              )}
            </Card>
          </ScrollView>
        </WebLayout>
        <RightDrawer
          visible={isTenantAdminDrawerVisible}
          onClose={handleCloseTenantAdminDrawer}
          title="Organisation admin"
        >
          <TenantAdminConfirmContent onConfirm={handleWebTenantAdminConfirm} />
        </RightDrawer>
        {removeAccessConfig && (
          <RightDrawer
            visible={isRemoveAccessDrawerVisible}
            onClose={handleCloseRemoveAccessDrawer}
            title={removeAccessConfig.title}
          >
            <RemoveAccessContent message={removeAccessConfig.message} onConfirm={handleWebRemoveAccessConfirm} />
          </RightDrawer>
        )}
      </>
    );
  }

  return (
    <>
      <Stack.Screen
        options={{
          title: user?.name ?? "Camera Access",
          headerBackButtonDisplayMode: "minimal",
          headerStyle: { backgroundColor: theme.colors.background },
          headerTitle: () => (
            <View flexDirection="column" alignItems="center" justifyContent="center" pr="7xl">
              <Text textStyle="lg/semibold" color="text">
                {titleScreen}
              </Text>
              <View>
                {user?.email && user?.name && (
                  <Text textStyle="xxs/regular" color="textMuted">
                    {user?.email}
                  </Text>
                )}
                {user?.phoneNumber && user?.email && user?.name && (
                  <Text textStyle="xxs/regular" color="textMuted">
                    {user?.phoneNumber}
                  </Text>
                )}
              </View>
            </View>
          ),
          headerRight: () => {
            const hasMenuActions = isEditUserEnabled || !isSelf;
            if (!hasMenuActions) return null;
            return (
              <PressableScale
                onPress={handleMenuPress}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                style={{ paddingHorizontal: 6, paddingVertical: 4 }}
              >
                <Icon name="kebabMenu" size={24} color={theme.colors.text} />
              </PressableScale>
            );
          },
        }}
      />
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.contentContainerStyle}>
        {isTenantAdmin && (
          <View
            bg="surfaceCard"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            style={styles.tenantAdminCard}
          >
            <Text textStyle="sm/regular" color="text">
              Organisation admin
            </Text>
            {!isSelf && (
              <PressableScale onPress={handleRemoveTenantAdmin}>
                <Icon name="delete" size={24} color={theme.colors.textSecondary} />
              </PressableScale>
            )}
          </View>
        )}
        {!isTenantAdmin && (
          <Section title="Site access">
            {isUpdatingRole ?
              <Animated.View entering={FadeIn} exiting={FadeOut}>
                <ActivityIndicator style={{ paddingVertical: theme.spacing["5xl"] }} />
              </Animated.View>
            : <Animated.View entering={FadeIn} exiting={FadeOut}>
                <UserSitesList
                  sites={userSites}
                  onSiteRoleChange={handleSiteRoleChange}
                  onDeleteSite={handleDeleteSite}
                />
              </Animated.View>
            }
          </Section>
        )}

        {!isTenantAdmin && (
          <Section title="Camera access">
            <UserCamerasList
              cameras={userCameras}
              onCameraRoleChange={handleCameraRoleChange}
              onDeleteCamera={handleDeleteCamera}
            />
          </Section>
        )}
        {!isTenantAdmin && (
          <Animated.View layout={LinearTransition}>
            <Button
              size="md"
              variant="primary"
              label="Add access"
              onPress={onPressAddAccess}
              iconRight={
                <Animated.View style={animatedChevronIconStyle}>
                  <Icon name="chevronDown" color={theme.colors.white.default} />
                </Animated.View>
              }
            />
            {showList && (
              <Animated.View entering={FadeIn} exiting={FadeOut} style={styles.listContainer}>
                <Pressable
                  onPress={handleTenantAdminPress}
                  style={({ pressed }) => [styles.listItem, pressed && styles.pressed]}
                >
                  <Text textStyle="xs/regular" color="textMuted">
                    Organisation admin
                  </Text>
                </Pressable>
                <Pressable
                  onPress={() =>
                    router.push({ pathname: "/settings/site-access/camera-access/assign-sites", params: { userId } })
                  }
                  style={({ pressed }) => [styles.listItem, pressed && styles.pressed]}
                >
                  <Text textStyle="xs/regular" color="textMuted">
                    Site access
                  </Text>
                </Pressable>
              </Animated.View>
            )}
          </Animated.View>
        )}
      </ScrollView>
      {user && (
        <EditUserBottomSheet bottomSheetRef={editUserBottomSheetRef} savedUser={user} onSave={handleEditUserSave} />
      )}
      <TenantAdminBottomSheet bottomSheetRef={tenantAdminBottomSheetRef} onConfirm={handleTenantAdminConfirm} />
      {removeAccessConfig && (
        <RemoveAccessBottomSheet
          bottomSheetRef={removeAccessBottomSheetRef}
          title={removeAccessConfig.title}
          message={removeAccessConfig.message}
          onConfirm={removeAccessConfig.onConfirm}
        />
      )}
    </>
  );
});

const styles = StyleSheet.create((theme) => ({
  scrollView: {
    flex: 1,
  },
  contentContainerStyle: {
    flexGrow: 1,
    padding: theme.spacing["3xl"],
    gap: theme.spacing["6xl"],
    paddingBottom: 120,
  },
  centered: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  tenantAdminCard: {
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing["2xl"],
  },
  listContainer: {
    position: "absolute",
    bottom: -80,
    left: 0,
    right: 0,
    backgroundColor: theme.colors.surfaceCard,
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    padding: theme.spacing.xs,
    marginTop: theme.spacing.sm,
  },
  listItem: {
    paddingVertical: theme.spacing.md,
    gap: theme.spacing.xxs,
    paddingHorizontal: theme.spacing.md,
  },
  pressed: {
    backgroundColor: theme.colors.surface1.foregroundTertiary,
    borderRadius: theme.spacing.sm,
  },
}));

export default CameraAccessScreen;
